import { useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { PsychologicalAssessment } from '@/domain/schema/v1';
import {
  isTciReport,
  parseTciReport,
  scaleLevel,
  subscaleLevel,
  TCI_SCALES,
  type TciResult,
} from '@/domain/assessment/tci';
import { ASSESSMENT_PROJECT_INSTRUCTION, readInterpretation } from '@/domain/ai/assessment';
import { parseStems } from '@/domain/assessment/sct';
import { AiBridge } from '@/services/aiBridge';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { useRequiredSession } from '../../state/session';
import { ConfirmButton } from '../../components/fields';
import { CopyButton } from '../../components/CopyButton';
import { ReceiveBox, SendButton } from '../../components/ExternalRoundTrip';
import type { ClientOutlet } from './ClientShell';

/**
 * 심리검사 (A35): 결과지 PDF를 넣으면 점수를 읽고(브라우저 안에서), 해석은 "심리검사 해석" 외부 AI 프로젝트에서 받는다.
 * 결과지의 이름·나이 같은 개인정보와 원본 PDF는 저장하지 않는다. 점수 요약과 해석은 문서·회기 자료에 들어간다.
 */
export function AssessmentsScreen() {
  const { client } = useOutletContext<ClientOutlet>();
  const svc = useService();
  const { data, reload } = useLoad(
    async () => ({ list: await svc.listAssessments(client.id), settings: await svc.getSettings() }),
    [svc, client.id],
  );
  if (!data) return <p className="muted">불러오는 중…</p>;
  const projectUrl = data.settings?.assessmentProjectUrl;
  return (
    <div className="stack">
      <p className="muted small">
        보통 1회기에 검사를 하고, 다음 회기에 해석 상담을 해요. 점수 요약과 확인한 해석은 사례보고서·상담기록지·회기
        분석 자료에 들어가요.
      </p>
      <UploadCard clientId={client.id} onSaved={reload} />
      <SctCard
        clientId={client.id}
        projectUrl={client.externalProjectUrl ?? projectUrl}
        stems={data.settings?.sctStems}
        onSaved={reload}
      />
      <AssessmentProjectBox onSaved={reload} collapsible />
      {data.list.length === 0 && <p className="muted">아직 넣은 검사가 없어요.</p>}
      {data.list.map((a) => (
        <AssessmentCard key={`${a.id}:${a.revision}`} a={a} projectUrl={projectUrl} onChanged={reload} />
      ))}
      <ManualAdd clientId={client.id} onSaved={reload} />
    </div>
  );
}

/** 결과지 PDF 넣기 → 읽은 점수 확인 → 저장 */
function UploadCard({ clientId, onSaved }: { clientId: string; onSaved: () => void }) {
  const svc = useService();
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<TciResult | null>(null);
  const read = (file: File) => {
    setBusy(true);
    setErr(null);
    setPreview(null);
    void import('../../export/pdfText')
      .then(({ readPdfTextItems }) => readPdfTextItems(file))
      .then((pages) => {
        if (!isTciReport(pages))
          throw new Error('지금은 TCI 결과지만 자동으로 읽어요. 다른 검사는 아래 "직접 입력"으로 넣으세요.');
        const r = parseTciReport(pages);
        if (!r.ok) throw new Error(r.error);
        setPreview(r.value);
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <div className="card">
      <strong>결과지 PDF 넣기</strong>
      <span className="muted small">
        TCI 결과지를 고르면 점수를 읽어요. 이름·나이 같은 개인정보와 PDF 파일은 저장하지 않아요.
      </span>
      <div className="row">
        <button type="button" className="soft-btn" disabled={busy} onClick={() => ref.current?.click()}>
          {busy ? '읽는 중…' : '결과지 고르기'}
        </button>
        <input
          ref={ref}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          aria-label="검사 결과지 PDF"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) read(f);
            e.target.value = '';
          }}
        />
      </div>
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
      {preview && (
        <div className="stack" style={{ width: '100%' }}>
          <p className="ok small">
            ✓ {preview.testName} · 검사일 {preview.administeredAt ?? '-'} · 규준 {preview.normGroup ?? '-'} · 무응답{' '}
            {preview.missingResponses ?? '-'} · 주 척도 {preview.scores.filter((s) => s.level === 'scale').length}개 ·
            하위척도 {preview.scores.filter((s) => s.level === 'subscale').length}개
          </p>
          <ScaleBars scores={preview.scores} />
          <div className="row">
            <button
              type="button"
              className="primary"
              onClick={() =>
                void svc
                  .saveTciResult(clientId, preview)
                  .then(() => {
                    setPreview(null);
                    onSaved();
                  })
                  .catch((e: Error) => setErr(e.message))
              }
            >
              이 점수로 저장
            </button>
            <button type="button" className="link" onClick={() => setPreview(null)}>
              취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** 주 척도: 백분위 막대 (30 이하 낮음, 70 이상 높음) */
function ScaleBars({ scores }: { scores: PsychologicalAssessment['scores'] }) {
  const main = (scores ?? []).filter((s) => s.level === 'scale');
  return (
    <div className="scale-bars">
      {main.map((s) => {
        const lv = scaleLevel(s.percentile);
        const group = TCI_SCALES.find((d) => d.key === s.key)?.group;
        return (
          <div key={s.key} className="scale-row">
            <span className="scale-name">
              {s.label}{' '}
              <span className="muted small">
                {s.key}
                {group ? ` · ${group}` : ''}
              </span>
            </span>
            <span className="scale-track" aria-hidden>
              <span className="scale-fill" style={{ width: `${Math.max(2, Math.min(100, s.percentile ?? 0))}%` }} />
              <span className="scale-mark" style={{ left: '30%' }} />
              <span className="scale-mark" style={{ left: '70%' }} />
            </span>
            <span className="scale-num">
              T{s.t ?? '-'} · {s.percentile ?? '-'}%
              {lv && lv !== '보통' ? (
                <span className={lv === '높음' ? 'status-pill' : 'status-pill warn'}>{lv}</span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function AssessmentCard({
  a,
  projectUrl,
  onChanged,
}: {
  a: PsychologicalAssessment;
  projectUrl?: string | undefined;
  onChanged: () => void;
}) {
  const svc = useService();
  const app = useRequiredSession();
  const ai = useMemo(() => new AiBridge(app.repo, app.ctx, svc), [app, svc]);
  const [request, setRequest] = useState('');
  const { data: packet } = useLoad(() => ai.assessmentPacket(a.id, request), [ai, a.id, a.revision, request]);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(a.interpretation ?? '');
  const subs = (a.scores ?? []).filter((s) => s.level === 'subscale');
  const approved = !!a.interpretationApprovedAt;
  return (
    <div className="card assessment-card">
      <div className="card-head" style={{ width: '100%' }}>
        <strong>{a.testName}</strong>
        <span className="muted small">
          {a.administeredAt ?? '날짜 없음'}
          {a.normGroup ? ` · 규준 ${a.normGroup}` : ''}
          {a.missingResponses !== undefined ? ` · 무응답 ${a.missingResponses}` : ''}
        </span>
      </div>
      {a.scores?.length ? <ScaleBars scores={a.scores} /> : <p className="small">{a.scoresSummary ?? '점수 없음'}</p>}
      {a.items?.length ? <ItemsEditor a={a} onChanged={onChanged} /> : null}
      {subs.length > 0 && (
        <details style={{ width: '100%' }}>
          <summary className="small">하위척도 {subs.length}개 (참고자료)</summary>
          <div className="table-scroll">
            <table className="form-table sub-table">
              <thead>
                <tr>
                  <th>척도</th>
                  <th>원점수</th>
                  <th>규준 M (SD)</th>
                  <th>규준 대비</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.key}>
                    <td>
                      <strong>{s.key}</strong> <span className="small">{s.label}</span>
                    </td>
                    <td>{s.raw}</td>
                    <td>
                      {s.mean} ({s.sd})
                    </td>
                    <td>{subscaleLevel(s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      <div className="interp" style={{ width: '100%' }}>
        <div className="row" style={{ alignItems: 'center' }}>
          <strong>해석</strong>
          {a.interpretation && (
            <span className={approved ? 'status-pill ok' : 'status-pill warn'}>{approved ? '확인함' : '확인 전'}</span>
          )}
          {a.interpretationSource === 'external-ai' && (
            <span className="muted small">외부 AI 해석 (해설 자료 근거)</span>
          )}
        </div>
        {a.interpretation && !editing && <InterpretationText text={a.interpretation} />}
        {editing && (
          <div className="stack">
            <textarea aria-label="해석 고치기" rows={12} value={text} onChange={(e) => setText(e.target.value)} />
            <div className="row">
              <button
                type="button"
                className="primary"
                onClick={() => void svc.setInterpretation(a.id, text).then(() => (setEditing(false), onChanged()))}
              >
                저장
              </button>
              <button type="button" className="link" onClick={() => setEditing(false)}>
                취소
              </button>
            </div>
          </div>
        )}
        {a.interpretation && !editing && (
          <div className="row">
            {!approved && (
              <button
                type="button"
                className="primary"
                onClick={() => void svc.approveInterpretation(a.id).then(onChanged)}
              >
                해석 확인
              </button>
            )}
            <button type="button" onClick={() => (setText(a.interpretation ?? ''), setEditing(true))}>
              고치기
            </button>
          </div>
        )}
        <details className="card" open={!a.interpretation}>
          <summary className="small">{a.interpretation ? '외부 AI에 다시 해석 받기' : '외부 AI로 해석 받기'}</summary>
          <label className="field">
            <span className="field-label">해석에서 특히 보고 싶은 점 (선택)</span>
            <textarea
              rows={2}
              value={request}
              onChange={(e) => setRequest(e.target.value)}
              placeholder="예: 관계 갈등과 연결해서, 해석 상담 문장을 더 쉽게"
            />
          </label>
          {packet && <SendButton text={packet} projectUrl={projectUrl} label="외부로 보내기 (심리검사 해석)" />}
          {!projectUrl && (
            <p className="muted small">설정에 "심리검사 해석" 프로젝트 주소를 넣으면 그 프로젝트가 바로 열려요.</p>
          )}
          <ReceiveBox
            title="해석 가져오기"
            hint="답 전체를 복사해 두면 돌아왔을 때 알아서 찾아요. 가져온 해석은 '확인 전'으로 들어가요."
            accept={(t) => readInterpretation(t).ok}
            onSubmit={(raw) => ai.importAssessmentInterpretation(a.id, raw).then(onChanged)}
          />
        </details>
      </div>
      <ConfirmButton
        className="link small"
        label="이 검사 지우기"
        confirmText="이 검사 기록을 지웁니다 (이력에는 남아요)."
        onConfirm={() => void svc.deleteAssessment(a.id).then(onChanged)}
      />
    </div>
  );
}

/**
 * 문장완성검사 올리기 (A42): 손글씨 검사지 사진·PDF를 외부 AI에 첨부해 문항별로 옮겨 적게 하고, 답을 가져와 기록한다.
 * 손글씨를 읽기 어려운 문항은 표시되고, 아래 문항별 응답에서 고친다.
 */
function SctCard({
  clientId,
  projectUrl,
  stems,
  onSaved,
}: {
  clientId: string;
  projectUrl?: string | undefined;
  stems?: string[] | undefined;
  onSaved: () => void;
}) {
  const svc = useService();
  const app = useRequiredSession();
  const ai = useMemo(() => new AiBridge(app.repo, app.ctx, svc), [app, svc]);
  const { data: packet } = useLoad(() => ai.sctReadPacket(clientId), [ai, clientId, stems?.length]);
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <div className="card stack">
      <strong>문장완성검사(SCT) 올리기</strong>
      <span className="muted small">
        손으로 쓴 검사지를 문항별 응답으로 옮겨 적어 기록해요. 손글씨는 외부 AI가 읽고, 읽기 어려운 문항은 표시돼서
        여기서 고칠 수 있어요. 문항별 응답은 사례보고서 별지로 들어가요.
      </span>
      <ol className="small sct-steps">
        <li>검사지를 쪽마다 사진 찍거나 스캔해 두세요. 이름·생년월일 칸은 가리고 찍으면 더 안전해요.</li>
        <li>
          <strong>외부로 보내기</strong>를 누르면 요청이 복사되고 프로젝트가 열려요. 입력창에 붙여넣고, 검사지 사진을
          모두 첨부해 보내세요.
        </li>
        <li>답 전체를 복사해 돌아오면 문항별 응답이 들어가요.</li>
      </ol>
      <SctStems stems={stems} onSaved={onSaved} />
      {packet && <SendButton text={packet} projectUrl={projectUrl} label="외부로 보내기 (SCT 옮겨 적기)" />}
      {msg && (
        <p className="ok small" role="status">
          {msg}
        </p>
      )}
      <ReceiveBox
        title="옮겨 적은 답 가져오기"
        hint="답 전체를 복사해 두면 돌아왔을 때 알아서 찾아요."
        accept={(t) => /SCT_ITEMS_V1/.test(t)}
        onSubmit={async (raw) => {
          const r = await ai.importSctItems(clientId, raw);
          const n = r.assessment.items?.length ?? 0;
          const unclear = r.assessment.items?.filter((x) => x.unclear).length ?? 0;
          setMsg(
            `${n}문항을 넣었어요${unclear ? ` · 손글씨 확인 필요 ${unclear}개` : ''}. 아래 검사 기록에서 확인하고 고치세요.${r.warnings.length ? ` (${r.warnings.join(' / ')})` : ''}`,
          );
          onSaved();
        }}
      />
    </div>
  );
}

/** 문장완성검사 문항 목록: 한 번 넣어 두면 옮겨 적을 때 번호·문항의 기준이 된다 (모든 내담자 공통) */
function SctStems({ stems, onSaved }: { stems?: string[] | undefined; onSaved: () => void }) {
  const svc = useService();
  const [text, setText] = useState((stems ?? []).join('\n'));
  const [saved, setSaved] = useState(false);
  return (
    <details className="sct-stems">
      <summary className="small">
        문항 목록{' '}
        {stems?.length
          ? `(${stems.length}문항 저장됨)`
          : '(보통 필요 없어요 — 검사지에 인쇄된 문항을 AI가 같이 읽어요)'}
      </summary>
      <p className="muted small">
        인쇄 상태가 나빠 문항이 잘 안 읽힐 때만 넣으세요. 한 줄에 한 문항 (번호는 있어도 없어도 돼요). 모든 내담자에게
        같이 써요.
      </p>
      <textarea
        aria-label="문장완성검사 문항 목록"
        rows={6}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setSaved(false);
        }}
      />
      <div className="row">
        <button
          type="button"
          onClick={() =>
            void svc.updateSettings({ sctStems: parseStems(text) }).then(() => {
              setSaved(true);
              onSaved();
            })
          }
        >
          문항 저장 ({parseStems(text).length}문항)
        </button>
        {saved && <span className="ok small">저장했어요</span>}
      </div>
    </details>
  );
}

/** 문항별 응답: 고치기, 손글씨 확인, 응답 확인 표시 */
function ItemsEditor({ a, onChanged }: { a: PsychologicalAssessment; onChanged: () => void }) {
  const svc = useService();
  const [items, setItems] = useState(a.items ?? []);
  const [dirty, setDirty] = useState(false);
  const [onlyUnclear, setOnlyUnclear] = useState(false);
  const unclear = items.filter((x) => x.unclear).length;
  const set = (no: number, patch: { response?: string; unclear?: boolean }) => {
    setItems((xs) =>
      xs.map((x) => {
        if (x.no !== no) return x;
        const { unclear: u, ...rest } = { ...x, ...patch };
        return u ? { ...rest, unclear: true } : rest;
      }),
    );
    setDirty(true);
  };
  const shown = onlyUnclear ? items.filter((x) => x.unclear) : items;
  return (
    <details className="sct-items" open>
      <summary>
        <strong>문항별 응답 {items.length}개</strong>{' '}
        {unclear > 0 && <span className="status-pill warn">손글씨 확인 필요 {unclear}</span>}{' '}
        <span className={a.itemsCheckedAt ? 'status-pill ok' : 'status-pill'}>
          {a.itemsCheckedAt ? '응답 확인함' : a.itemsSource === 'external-ai' ? 'AI가 옮김 · 확인 전' : '확인 전'}
        </span>
      </summary>
      {unclear > 0 && (
        <label className="check small">
          <input type="checkbox" checked={onlyUnclear} onChange={(e) => setOnlyUnclear(e.target.checked)} /> 확인 필요한
          문항만 보기
        </label>
      )}
      <ol className="sct-list">
        {shown.map((x) => (
          <li key={x.no} className={x.unclear ? 'sct-row unclear' : 'sct-row'}>
            <span className="sct-no">{x.no}</span>
            <div className="sct-body">
              <span className="sct-stem">{x.stem || '(문항 없음)'}</span>
              <textarea
                className="item-text"
                aria-label={`${x.no}번 응답`}
                rows={1}
                value={x.response}
                onChange={(e) => set(x.no, { response: e.target.value })}
              />
              {x.unclear && (
                <button type="button" className="link small" onClick={() => set(x.no, { unclear: false })}>
                  손글씨 확인함
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>
      <div className="row">
        {dirty && (
          <button
            type="button"
            className="primary"
            onClick={() => void svc.updateAssessmentItems(a.id, items).then(onChanged)}
          >
            고친 응답 저장
          </button>
        )}
        {!dirty && !a.itemsCheckedAt && (
          <button type="button" className="primary" onClick={() => void svc.checkAssessmentItems(a.id).then(onChanged)}>
            응답 확인했어요{unclear ? ` (확인 필요 ${unclear}개 남음)` : ''}
          </button>
        )}
      </div>
    </details>
  );
}

/** ### 제목이 있는 해석 글을 읽기 좋게 */
function InterpretationText({ text }: { text: string }) {
  return (
    <div className="interp-text">
      {text.split('\n').map((l, i) => {
        const h = /^#{1,6}\s*(.+)$/.exec(l);
        if (h) return <h4 key={i}>{h[1]}</h4>;
        return l.trim() ? <p key={i}>{l}</p> : null;
      })}
    </div>
  );
}

/** 다른 검사 직접 입력 (MMPI-2 등, 자동 읽기 전까지) */
function ManualAdd({ clientId, onSaved }: { clientId: string; onSaved: () => void }) {
  const svc = useService();
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [summary, setSummary] = useState('');
  const [interp, setInterp] = useState('');
  return (
    <details className="card">
      <summary>
        <strong>직접 입력</strong> <span className="muted small">다른 검사 (점수 요약·해석을 직접)</span>
      </summary>
      <label>
        검사 이름
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: MMPI-2" />
      </label>
      <label>
        검사일
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <label>
        점수 요약
        <textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
      </label>
      <label>
        해석 (선택)
        <textarea rows={4} value={interp} onChange={(e) => setInterp(e.target.value)} />
      </label>
      <button
        type="button"
        className="primary"
        disabled={!name.trim()}
        onClick={() =>
          void svc
            .addAssessment(clientId, {
              testName: name.trim(),
              ...(date ? { administeredAt: date } : {}),
              scoresSummary: summary,
              interpretation: interp,
            })
            .then(() => {
              setName('');
              setDate('');
              setSummary('');
              setInterp('');
              onSaved();
            })
        }
      >
        추가
      </button>
    </details>
  );
}

/** "심리검사 해석" 외부 AI 프로젝트: 주소 저장 + 프로젝트 지침 복사 (설정에도 같은 것) */
export function AssessmentProjectBox({
  onSaved,
  collapsible,
}: {
  onSaved?: () => void;
  /** 연결돼 있으면 접어 둔다 (심리검사 탭) */ collapsible?: boolean;
}) {
  const svc = useService();
  const { data, reload } = useLoad(() => svc.ensureSettings(), [svc]);
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const value = url ?? data?.assessmentProjectUrl ?? '';
  const saved = data?.assessmentProjectUrl;
  const inner = (
    <>
      <span className="muted small">
        외부 AI에 "심리검사 해석" 프로젝트를 하나 만들고, 해설 자료(검사 요강·해석 지침 PDF)를 올리고, 아래 지침을
        프로젝트 지침에 붙여넣으세요. 그 프로젝트 주소를 넣어 두면 해석 받을 때 바로 열려요.
      </span>
      <CopyButton text={ASSESSMENT_PROJECT_INSTRUCTION} label="해석 프로젝트 지침 복사" />
      <label>
        프로젝트 주소
        <input
          value={value}
          placeholder="https://claude.ai/project/…"
          onChange={(e) => {
            setUrl(e.target.value);
            setOk(false);
          }}
        />
      </label>
      <button
        type="button"
        disabled={!value.trim() || value === (data?.assessmentProjectUrl ?? '')}
        onClick={() => {
          setErr(null);
          const v = value.trim();
          if (!/^https:\/\//.test(v)) {
            setErr('https:// 로 시작하는 주소를 넣어 주세요');
            return;
          }
          void svc
            .updateSettings({ assessmentProjectUrl: v })
            .then(() => {
              setOk(true);
              reload();
              onSaved?.();
            })
            .catch((e: Error) => setErr(e.message));
        }}
      >
        주소 저장
      </button>
      {ok && <span className="ok small">저장했어요</span>}
      {err && (
        <span role="alert" className="warn small">
          {err}
        </span>
      )}
    </>
  );
  if (collapsible && saved)
    return (
      <details className="card">
        <summary>
          <strong>심리검사 해석 프로젝트</strong> <span className="ok small">연결됨</span>{' '}
          <span className="muted small">{saved.length > 48 ? `${saved.slice(0, 48)}…` : saved}</span>
        </summary>
        {inner}
      </details>
    );
  return (
    <div className="card">
      <strong>심리검사 해석 프로젝트</strong>
      {inner}
    </div>
  );
}
